import { isEqual, merge } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewQueues from '@/shared/api/fulfilment/queue/viewQueues';
import viewProductVariants from '@/shared/api/product/viewProductVariants';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Queue from '@/shared/types/fulfilment/queue.type';

import Package from '../../PackagingDetailsTableForm_v2/types/package.type';
import ProductTableRow from '../types/productTableRow.type';

type Args = {
  selectedQueues: Queue[];
  packages: Package[];
  handleAssignItemsToPackage: (value: {
    packageId: string;
    assignedItems: number;
  }) => void;
  getProductTableData: (data: ProductTableRow[]) => void;
};

const useCustomsProductTableForm = ({
  packages,
  selectedQueues,
  handleAssignItemsToPackage,
  getProductTableData,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();

  const [table, setTable] = useState({
    data: [] as ProductTableRow[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: [
        'product_id',
        'variant_id',
        'product_qty',
        'product_name',
        'origin',
        'unit_price',
        'weight',
        'product_description',
        'assign_package',
      ],
      tableRequiredColumns: [
        'product_id',
        'variant_id',
        'unit_price',
        'weight',
        'product_name',
        'product_description',
        'assign_package',
        'origin',
        'product_qty',
      ],
    });

  const tableStateRequestBodyRef = useRef<typeof requestBody | null>(null);
  const tableStateBodyRef = useRef<(typeof table)['data'] | null>(null);

  const handleUpdateTableRow = (value: {
    rowId: string;
    value: Partial<ProductTableRow>;
  }) => {
    setTable((prev) => ({
      ...prev,
      data: prev.data.map((row) => {
        if (row.fulfilment_queue_id === value.rowId) {
          return merge({}, row, value.value);
        }
        return row;
      }),
    }));
  };

  useEffect(() => {
    if (!isEqual(table.data, tableStateBodyRef.current)) {
      tableStateBodyRef.current = table.data;

      const aggregatedAssignedPackages = table.data.reduce(
        (acc, item) => {
          if (acc[item.assign_package]) {
            acc[item.assign_package] = acc[item.assign_package] + 1;
          } else {
            acc[item.assign_package] = 1;
          }

          return acc;
        },
        {} as Record<string, number>,
      );

      Object.entries(aggregatedAssignedPackages).forEach(([id, assigned]) =>
        handleAssignItemsToPackage({
          packageId: id,
          assignedItems: assigned,
        }),
      );
    }
  }, [handleAssignItemsToPackage, table.data]);

  useEffect(() => {
    const fetchVariants = async (queues: Queue[]) => {
      const response = await viewProductVariants({
        body: {
          columns: ['variant_name'],
          search: [
            {
              variant_id: {
                cop: ['in'],
                value: queues.map((queue) => queue.variant_id),
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        return response.data;
      }
      return [];
    };

    const fetchQueues = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const requiredFilters = [
        {
          fulfilment_queue_id: {
            cop: ['in'],
            value: selectedQueues.map((queue) => queue.fulfilment_queue_id),
          },
        },
      ];

      const restFilters =
        requestBody.search.length > 0
          ? requestBody.search.map((item, index) =>
              index === 0 ? { ...item, op: 'and' } : item,
            )
          : undefined;

      const response = await viewQueues(
        { search: [...requiredFilters, restFilters] },
        redirectLogin,
      );

      if (response.success) {
        const variants = await fetchVariants(response.data);

        const queuesVariantsMerged = merge(response.data, variants);

        const tableRowsList = queuesVariantsMerged.map((item) => {
          const rowInList = table.data.find(
            (item) => item.fulfilment_queue_id === item.fulfilment_queue_id,
          );

          if (rowInList) {
            return merge(item, rowInList);
          }

          return merge(
            {
              unit_price: {
                currency: 'CAD',
                value: (
                  parseFloat(item.pre_tax_price) / parseFloat(item.product_qty)
                ).toFixed(2),
              },
              weight: {
                unit: 'oz',
                value: 1,
              },
              assign_package: packages.at(0)?.package_id,
              country_of_origin: 'CA',
            },
            item,
          );
        }) as ProductTableRow[];

        setTable((prev) => ({
          ...prev,
          isLoading: false,
          data: tableRowsList,
        }));
        getProductTableData(tableRowsList);
      }
    };

    if (!isEqual(requestBody, tableStateRequestBodyRef.current)) {
      tableStateRequestBodyRef.current = requestBody;
      void fetchQueues();
    }
  }, [
    packages,
    selectedQueues,
    requestBody,
    table.data,
    redirectLogin,
    getProductTableData,
  ]);

  return {
    packages,
    apiRef,
    model,
    columns,
    table,
    visibilityModel,
    setVisibilityModel,
    handleUpdateTableRow,
  };
};

export default useCustomsProductTableForm;
